import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../services/api';
import { subscribeToNotifications } from '../../services/notificationsWS';
import './NotificationsPanel.css';

const NotificationsPanel = () => {
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const panelRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                // Загружаем последние уведомления и фильтруем только непрочитанные для колокольчика
                const response = await API.notifications.getNotifications();
                const data = response.data.results || response.data;
                const unreadOnly = data.filter(n => !n.read);
                setNotifications(unreadOnly.slice(0, 10)); // Показываем до 10 новых
                setUnreadCount(unreadOnly.length);
            } catch (err) {
                console.error('Failed to fetch notifications', err);
            }
        };

        fetchNotifications();

        // WebSocket для мгновенного обновления списка
        let socket = null;
        const onNewNotification = (event) => {
            if (event.type === 'notification_created') {
                const newNotif = event.data;
                setNotifications(prev => {
                    if (prev.some(n => n.id === newNotif.id)) return prev;
                    return [newNotif, ...prev].slice(0, 10);
                });
                setUnreadCount(prev => prev + 1);
            } else if (event.type === 'notifications_resolved') {
                const { notification_ids, related_object_id, notif_type } = event.data;
                
                // Если уведомления разрешены (помечены как прочитанные), 
                // мы просто удаляем их из списка колокольчика (так как там только новые)
                setNotifications(prev => {
                    const nextNotifs = prev.filter(n => {
                        const isTarget = notification_ids?.includes(n.id) || 
                            (n.related_object_id === related_object_id && n.notif_type === notif_type);
                        return !isTarget;
                    });
                    
                    const removedCount = prev.length - nextNotifs.length;
                    if (removedCount > 0) {
                        setUnreadCount(count => Math.max(0, count - removedCount));
                    }
                    return nextNotifs;
                });
            }
        };

        socket = subscribeToNotifications(onNewNotification);

        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            if (socket) socket.close();
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleMarkAsRead = async (notif) => {
        if (notif.read) {
            // Если уже прочитано, просто переходим
            if (notif.target_url) {
                navigate(notif.target_url);
                setIsOpen(false);
            }
            return;
        }

        try {
            await API.notifications.markAsRead(notif.id);
            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
            
            // Если есть куда переходить - переходим
            if (notif.target_url) {
                navigate(notif.target_url);
                setIsOpen(false);
            }
        } catch (err) {
            console.error('Failed to mark as read', err);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await API.notifications.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to mark all as read', err);
        }
    };

    return (
        <div className="notifications-container" ref={panelRef}>
            <button
                className={`notifications-trigger ${unreadCount > 0 ? 'has-unread' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title="Уведомления"
            >
                <span className="bell-icon">🔔</span>
                {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
            </button>

            {isOpen && (
                <div className="notifications-panel">
                    <div className="panel-header">
                        <h3>Уведомления</h3>
                        {unreadCount > 0 && (
                            <button className="mark-all-btn" onClick={handleMarkAllAsRead}>
                                Прочитать все
                            </button>
                        )}
                    </div>
                    <div className="panel-content">
                        {notifications.length === 0 ? (
                            <div className="no-notifications">У вас пока нет уведомлений</div>
                        ) : (
                            <div className="notifications-list">
                                {notifications.map(notif => (
                                    <div
                                        key={notif.id}
                                        className={`notification-item ${notif.read ? 'read' : 'unread'} priority-${notif.priority} ${notif.target_url ? 'clickable' : ''}`}
                                        onClick={() => handleMarkAsRead(notif)}
                                    >
                                        <div className="notif-header">
                                            <span className={`notif-type-tag type-${notif.notif_type}`}>
                                                {notif.payload?.type?.includes('case') ? '💼 Кейс' :
                                                    notif.notif_type === 'system' ? '⚙️ Система' :
                                                        notif.notif_type === 'track' ? '🛤️ Трек' :
                                                            notif.notif_type === 'project' ? '🚀 Проект' :
                                                                notif.notif_type === 'stage' ? '📅 Этап' : '💬 Сообщение'}
                                            </span>
                                            <span className="notif-time">
                                                {notif.created_at_display || new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div className="notif-message">{notif.message}</div>

                                        {notif.target_url && (
                                            <div className="notif-hint">Нажмите, чтобы перейти</div>
                                        )}

                                        {!notif.read && <div className="unread-dot"></div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="panel-footer">
                        <button className="view-all-btn" onClick={() => { navigate('/notifications'); setIsOpen(false); }}>
                            Смотреть все уведомления
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsPanel;
